<template>
	<div class="transactions-map-filters">
		<section v-if="showClassificationFilters" class="filters">
			<h5>{{ t("filters.classification-title") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="assetTypes"
					:options="assetTypeSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.asset-type')"
					:placeholder="$t('filters.all-masculine')"
					@change="captureFilterChange('filters.asset-type', $event)"
				/>
			</div>

			<div v-if="shouldShowLandCategoryFilter" class="content filters">
				<ArkListboxDropdown
					v-model="landCategories"
					:options="landCategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-category')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableLandCategoryFilter"
					@change="captureFilterChange('filters.land-category', $event)"
				/>
			</div>

			<div v-if="shouldShowLandCategoryFilter" class="content filters">
				<ArkListboxDropdown
					v-model="landSubcategories"
					:options="landSubcategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-subcategory')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableSubcategoryFilter"
					@change="captureFilterChange('filters.land-subcategory', $event)"
				/>
			</div>

			<div v-if="shouldShowBuiltCategoryFilter" class="content filters">
				<ArkListboxDropdown
					v-model="builtCategories"
					:options="builtCategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.built-category')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBuiltCategoryFilter"
					@change="captureFilterChange('filters.built-category', $event)"
				/>
			</div>

			<div v-if="shouldShowBuiltCategoryFilter" class="content filters">
				<ArkListboxDropdown
					v-model="builtSubcategories"
					:options="builtSubcategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.built-subcategory')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBuiltSubcategoryFilter"
					@change="captureFilterChange('filters.built-subcategory', $event)"
				/>
			</div>
		</section>

		<section v-if="store.currentUser?.has(Permission.TransactionRead)" class="filters">
			<h5>{{ t("filters.transaction") }}</h5>
			<div v-if="store.currentUser?.has(Permission.TransactionWrite)" class="content filters">
				<ArkListboxDropdown
					v-model="transactionStatuses"
					:options="transactionStatusSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.transaction-status-title')"
					:placeholder="$t('filters.all-masculine')"
					:searchNormalizer="normalizeLabel"
					@change="captureFilterChange('filters.transaction-status-title', $event)"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionVerificationStatus"
					:options="transactionVerificationStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-verification-status-title')"
					:placeholder="$t('filters.all-masculine')"
					@change="captureFilterChange('filters.transaction-verification-status-title', $event)"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionBuyerStatus"
					:options="buyerStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.buyer-status-title')"
					:placeholder="$t('filters.all-masculine')"
					@change="captureFilterChange('filters.buyer-status-title', $event)"
				/>
			</div>
			<div v-if="store.currentUser?.has(Permission.TransactionWrite)" class="content filters">
				<ArkListboxDropdown
					v-model="transactionSource"
					:options="transactionSourceSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-source-title')"
					:placeholder="$t('filters.all-feminine')"
					@change="captureFilterChange('filters.transaction-source-title', $event)"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionComposition"
					:options="transactionCompositionSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-composition-title')"
					:placeholder="$t('filters.all-feminine')"
					@change="captureFilterChange('filters.transaction-composition-title', $event)"
				/>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.others-title") }}</h5>
			<div class="content range-filters">
				<div class="range superficy">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="superficyRange"
							:label="$t('opportunities.unitFields.superficy')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10000, 50000, 250000]"
							@change="captureRangeFilterChange('opportunities.superficy', $event)"
						/>
					</div>
				</div>

				<div class="range constructible">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="constructibleRange"
							:label="$t('opportunities.unitFields.constructible')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[15000, 50000, 200000]"
							@change="captureRangeFilterChange('opportunities.constructible', $event)"
						/>
					</div>
				</div>

				<div class="range price">
					<div class="values">
						<RangeNotchedSlider
							v-model="priceRange"
							:label="$t('opportunities.price')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[250000, 1000000, 5000000]"
							unit-prefix="$"
							@change="captureRangeFilterChange('opportunities.price', $event)"
						/>
					</div>
				</div>

				<div class="range residential-units">
					<div class="values">
						<RangeNotchedSlider
							v-model="residentialUnitsRange"
							:label="$t('opportunities.residentialUnits')"
							:min="residentialUnitsSliderMin"
							:max="residentialUnitsSliderMax"
							:thresholds="[15, 40, 100]"
							@change="captureRangeFilterChange('opportunities.residentialUnits', $event)"
						/>
					</div>
				</div>

				<div v-if="isUserLoggedIn && currentUser.has(Permission.TransactionRead)" class="range date">
					<ArkDateRangePicker
						:start-date="dateRange.min ?? undefined"
						:end-date="dateRange.max ?? undefined"
						@date-range-change="onDateRangeChange"
					/>
				</div>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("opportunities.locationAndType") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="regions"
					:options="regionSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.regions-title')"
					:placeholder="$t('opportunities.all-regions')"
					:searchNormalizer="normalizeLabel"
					@change="captureFilterChange('filters.regions-title', $event)"
				/>

				<ArkListboxDropdown
					v-model="municipalities"
					:options="municipalitySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.municipalities-title')"
					:placeholder="$t('opportunities.all-cities')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableMunicipalityFilter"
					@change="captureFilterChange('filters.municipalities-title', $event)"
				/>

				<ArkListboxDropdown
					v-model="boroughs"
					:options="boroughSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.boroughs-title')"
					:placeholder="$t('opportunities.all-boroughs')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBoroughFilter"
					@change="captureFilterChange('filters.boroughs-title', $event)"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia"
import { computed, ref, watch, onMounted, toRaw, nextTick, inject } from "vue"
import { useI18n } from "vue-i18n"
import { config, Feature } from "@/AppConfig.ts"
import AreaRangeNotchedSlider from "@/components/ui/AreaRangeNotchedSlider.vue"
import ArkDateRangePicker from "@/components/ui/ArkDateRangePicker.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import { RANGE_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MIN } from "@/components/constants"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import Button from "@/components/ui/Button.vue"
import { useNavigationContext } from "@/composables/useNavigationContext"
import { useView } from "@/composables/useView"
import { AssetType, LandCategory, LandSubcategory } from "@/graphql"
import { translatedSelectOption } from "@/lang/i18n"
import {
	type BoroughRecord,
	type Municipality,
	TransactionComposition,
	TransactionMetadataBuyerStatus,
	TransactionMetadataStatus,
	TransactionSource,
} from "@/model/DataModel.ts"
import { Permission, UseRootStore } from "@/model/RootStore"
import { ProjectStoreKey } from "@/stores/projectStore"
import { TransactionVerificationStatus, ViewScope } from "@/types"
import { areEqualIgnoringOrder } from "@/utils"
import { builtCategoryValues, builtSubcategories as builtSubcategoryValues } from "@/utils/builtAssetClassification"
import { transactionMetadataStatusTranslationKeys } from "@/utils/filterTranslationKeys"
import { analytics } from "@/utils/analytics"

const emit = defineEmits<{ "user-change": [] }>()

const { t } = useI18n()
const captureFilterChange = analytics.filterChanged
const captureRangeFilterChange = analytics.rangeFilterChanged
const store = UseRootStore()
const { isUserLoggedIn, currentUser } = storeToRefs(store)
const view = useView(ViewScope.TransactionsMap)
const huntingView = useView(ViewScope.TransactionsHuntingMap)
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const { isMapMode, isHuntingSection } = useNavigationContext({ currentProject })
const activeView = computed(() => (isMapMode.value && isHuntingSection.value ? huntingView : view))

let isApplyingViewFilter = false
const assetTypes = ref<string[]>([])
const builtCategories = ref<string[]>([])
const builtSubcategories = ref<string[]>([])
const landCategories = ref<string[]>([])
const landSubcategories = ref<string[]>([])
const transactionStatuses = ref<string[]>([])
const transactionVerificationStatus = ref<TransactionVerificationStatus | null>(null)
const transactionBuyerStatus = ref<string | null>(null)
const transactionSource = ref<string | null>(null)
const transactionComposition = ref<string | null>(null)
const superficyRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const constructibleRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const priceRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const residentialUnitsSliderMin = RESIDENTIAL_UNITS_SLIDER_MIN
const residentialUnitsSliderMax = RESIDENTIAL_UNITS_SLIDER_MAX
const residentialUnitsRange = ref<[number, number]>([residentialUnitsSliderMin, residentialUnitsSliderMax])
const dateRange = ref<{ min: Date | null; max: Date | null }>({ min: null, max: null })
const regions = ref<string[]>([])
const municipalities = ref<string[]>([])
const boroughs = ref<string[]>([])

function onDateRangeChange(data: { start: Date | null; end: Date | null }): void {
	analytics.dateFilterChanged(
		{ start: dateRange.value.min, end: dateRange.value.max },
		{ start: data.start, end: data.end }
	)

	dateRange.value = { min: data.start, max: data.end }
}

function rangeValue(
	range: { min?: number | null; max?: number | null } | undefined,
	defaultRange: [number, number]
): [number, number] {
	return [range?.min ?? defaultRange[0], range?.max ?? defaultRange[1]]
}

function dateValue(value: string | null | undefined): Date | null {
	return value ? new Date(`${value}T00:00:00`) : null
}

function syncFilterValue<T>(source: { value: T }, update: (value: T) => void): void {
	watch(
		source,
		(value, oldValue) => {
			if (isApplyingViewFilter) return
			update(value)
			if (!areEqualIgnoringOrder(value, oldValue)) {
				emit("user-change")
			}
		},
		{ deep: true }
	)
}

// transaction statuses -> multi
const transactionStatusSelectOptions = computed(() =>
	Object.values(TransactionMetadataStatus).map(value =>
		translatedSelectOption(value, transactionMetadataStatusTranslationKeys[value])
	)
)

const buyerStatusSelectOptions = computed(() =>
	Object.values(TransactionMetadataBuyerStatus).map(value =>
		translatedSelectOption(value, `filters.buyer-status-${value}`)
	)
)

const transactionVerificationStatusSelectOptions = computed(() => [
	translatedSelectOption(TransactionVerificationStatus.Verified, "filters.transaction-verification-status-verified"),
	translatedSelectOption(
		TransactionVerificationStatus.Unverified,
		"filters.transaction-verification-status-unverified"
	),
])

const transactionSourceSelectOptions = computed(() =>
	(Object.values(TransactionSource) as string[]).map(value =>
		translatedSelectOption(value, `filters.transaction-source-${value}`)
	)
)

const transactionCompositionSelectOptions = computed(() =>
	(Object.values(TransactionComposition) as string[]).map(value =>
		translatedSelectOption(value, `filters.transaction-composition-${value}`)
	)
)

// regions / municipalities / boroughs -> multi (options come from real reference data, selection is local)
const regionSelectOptions = computed(() => regionItems.value.map(o => ({ value: o.id, label: o.label })))

const municipalitySelectOptions = computed(() => municipalityItems.value.map(o => ({ value: o.id, label: o.label })))

const boroughSelectOptions = computed(() => boroughItems.value.map(o => ({ value: o.id, label: o.label })))

const selectedRegions = computed(() => new Set<string>(regions.value))

const selectedMunicipalities = computed(() => new Set<string>(municipalities.value))

const shouldDisableMunicipalityFilter = computed(() => selectedRegions.value.size === 0)

const shouldDisableBoroughFilter = computed(() => selectedMunicipalities.value.size === 0)

const availableRegionNames = ref<string[]>([])
const availableMunicipalityRecords = ref<Municipality[]>([])
const availableBoroughRecords = ref<BoroughRecord[]>([])

// classification filters
const showClassificationFilters = computed(() => config.ui.has(Feature.DevTypes))
const shouldDisableLandCategoryFilter = computed(() => {
	if (!assetTypes.value.length) return false
	return !assetTypes.value.includes(AssetType.Land)
})

const shouldShowLandCategoryFilter = computed(() => {
	return assetTypes.value.includes(AssetType.Land)
})

const shouldShowSubcategoryFilter = computed(() => {
	if (!shouldShowLandCategoryFilter.value) return false
	if (!landCategories.value.length) return false

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const shouldDisableSubcategoryFilter = computed(() => {
	if (shouldDisableLandCategoryFilter.value) return true
	if (!landCategories.value.length) return true

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return !landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const assetTypeSelectOptions = computed(() => [
	...Object.values(AssetType).map(value => translatedSelectOption(value, `property.assetType.${value}`)),
	translatedSelectOption("none", "property.assetType.none"),
])

const shouldDisableBuiltCategoryFilter = computed(() => !assetTypes.value.includes(AssetType.Built))
const shouldShowBuiltCategoryFilter = computed(() => assetTypes.value.includes(AssetType.Built))

const builtCategorySelectOptions = computed(() =>
	builtCategoryValues.map(value => translatedSelectOption(value, `property.builtCategory.${value}`))
)

const builtSubcategorySelectOptions = computed(() =>
	builtSubcategoryValues(builtCategories.value).map(value =>
		translatedSelectOption(value, `property.builtSubcategory.${value}`)
	)
)

const shouldDisableBuiltSubcategoryFilter = computed(
	() => shouldDisableBuiltCategoryFilter.value || builtSubcategorySelectOptions.value.length === 0
)

const landCategorySelectOptions = computed(() =>
	Object.values(LandCategory).map(value => translatedSelectOption(value, `property.landCategory.${value}`))
)

const landSubcategorySelectOptions = computed(() => {
	if (!landCategories.value.length) return []

	const options = []

	// Residential
	if (landCategories.value.includes(LandCategory.Residential)) {
		options.push(
			translatedSelectOption(LandSubcategory.LowRise, "property.landSubcategory.LowRise"),
			translatedSelectOption(LandSubcategory.MidRise, "property.landSubcategory.MidRise"),
			translatedSelectOption(LandSubcategory.HighRise, "property.landSubcategory.HighRise"),
			translatedSelectOption(LandSubcategory.TownHouse, "property.landSubcategory.TownHouse"),
			translatedSelectOption(LandSubcategory.SingleFamily, "property.landSubcategory.SingleFamily")
		)
	}

	// Commercial
	if (landCategories.value.includes(LandCategory.Commercial)) {
		options.push(
			translatedSelectOption(LandSubcategory.LowRise, "property.landSubcategory.LowRise"),
			translatedSelectOption(LandSubcategory.MidRise, "property.landSubcategory.MidRise"),
			translatedSelectOption(LandSubcategory.HighRise, "property.landSubcategory.HighRise")
		)
	}

	// Industrial
	if (landCategories.value.includes(LandCategory.Industrial)) {
		options.push(
			translatedSelectOption(LandSubcategory.LightMedium, "property.landSubcategory.LightMedium"),
			translatedSelectOption(LandSubcategory.Heavy, "property.landSubcategory.Heavy")
		)
	}

	// Agricultural
	if (landCategories.value.includes(LandCategory.Agricultural)) {
		options.push(
			translatedSelectOption(LandSubcategory.FullyCultivated, "property.landSubcategory.FullyCultivated"),
			translatedSelectOption(LandSubcategory.MostlyCultivated, "property.landSubcategory.MostlyCultivated"),
			translatedSelectOption(LandSubcategory.HalfAndHalf, "property.landSubcategory.HalfAndHalf"),
			translatedSelectOption(LandSubcategory.MostlyForestry, "property.landSubcategory.MostlyForestry"),
			translatedSelectOption(LandSubcategory.FullForestry, "property.landSubcategory.FullForestry")
		)
	}

	const seen = new Set()
	return options.filter(option => {
		if (seen.has(option.value)) return false
		seen.add(option.value)
		return true
	})
})

onMounted(async () => {
	const [regionNames, municipalityRecords, loadedBoroughs] = await Promise.all([
		store.getRegions,
		store.getMunicipalityRecords,
		store.getBoroughRecords,
	])
	availableRegionNames.value = regionNames
	availableMunicipalityRecords.value = municipalityRecords
	availableBoroughRecords.value = loadedBoroughs
})

watch(landCategories, () => {
	if (!shouldShowSubcategoryFilter.value) {
		landSubcategories.value = []
		return
	}

	// Keep only valid subcategories
	if (landSubcategories.value.length) {
		const validValues: string[] = landSubcategorySelectOptions.value.map(o => o.value)
		landSubcategories.value = landSubcategories.value.filter(sub => validValues.includes(sub))
	}
})

watch(assetTypes, () => {
	if (!shouldShowLandCategoryFilter.value) {
		landCategories.value = []
		landSubcategories.value = []
	}

	if (shouldDisableBuiltCategoryFilter.value) {
		builtCategories.value = []
		builtSubcategories.value = []
	}
})

watch([builtCategories, builtSubcategorySelectOptions], () => {
	const validValues = builtSubcategorySelectOptions.value.map(option => option.value)
	builtSubcategories.value = builtSubcategories.value.filter(value => validValues.includes(value))
})

// Normalize a label by removing accents and lowercasing it
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
function normalizeLabel(name: string) {
	return name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

const regionItems = computed(() =>
	toRaw(availableRegionNames.value).map(name => ({
		id: name,
		label: name,
	}))
)

const hasMunicipalityRegionData = computed(() =>
	availableMunicipalityRecords.value.some(municipality => municipality.regionName)
)

const municipalityItems = computed(() => {
	const municipalityRecords = toRaw(availableMunicipalityRecords.value)
	const filteredMunicipalities = hasMunicipalityRegionData.value
		? municipalityRecords.filter(
				municipality => municipality.regionName && selectedRegions.value.has(municipality.regionName)
			)
		: municipalityRecords

	return filteredMunicipalities
		.map(municipality => ({
			id: municipality.name,
			label: municipality.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
})

const boroughItems = computed(() =>
	toRaw(availableBoroughRecords.value)
		.filter(borough => borough.municipalityName && selectedMunicipalities.value.has(borough.municipalityName))
		.map(borough => ({
			id: borough.name,
			label: borough.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
)

function filterValuesByOptions(values: string[], options: { value: string }[]): string[] {
	const validValues = new Set(options.map(option => option.value))
	return values.filter(value => validValues.has(value))
}

watch([regions, () => municipalitySelectOptions.value.map(option => option.value)], () => {
	municipalities.value = shouldDisableMunicipalityFilter.value
		? []
		: filterValuesByOptions(municipalities.value, municipalitySelectOptions.value)
})

watch([municipalities, () => boroughSelectOptions.value.map(option => option.value)], () => {
	boroughs.value = shouldDisableBoroughFilter.value
		? []
		: filterValuesByOptions(boroughs.value, boroughSelectOptions.value)
})

syncFilterValue(assetTypes, value => activeView.value.setTransactionAssetTypes(value))
syncFilterValue(builtCategories, value => activeView.value.setTransactionBuiltCategories(value))
syncFilterValue(builtSubcategories, value => activeView.value.setTransactionBuiltSubcategories(value))
syncFilterValue(landCategories, value => activeView.value.setTransactionLandCategories(value))
syncFilterValue(landSubcategories, value => activeView.value.setTransactionLandSubcategories(value))
syncFilterValue(transactionStatuses, value => activeView.value.setTransactionStatuses(value))
syncFilterValue(transactionVerificationStatus, value => activeView.value.setTransactionVerificationStatus(value))
syncFilterValue(transactionBuyerStatus, value => activeView.value.setTransactionBuyerStatus(value))
syncFilterValue(transactionSource, value => activeView.value.setTransactionSource(value))
syncFilterValue(transactionComposition, value => activeView.value.setTransactionComposition(value))
syncFilterValue(superficyRange, value => activeView.value.setTransactionLandAreaRange(value))
syncFilterValue(constructibleRange, value => activeView.value.setTransactionBuildableLandAreaRange(value))
syncFilterValue(priceRange, value => activeView.value.setTransactionPriceRange(value))
syncFilterValue(residentialUnitsRange, value => activeView.value.setTransactionResidentialUnitsRange(value))
syncFilterValue(dateRange, value => activeView.value.setTransactionDateRange(value))
syncFilterValue(regions, value => activeView.value.setTransactionRegions(value))
syncFilterValue(municipalities, value => activeView.value.setTransactionMunicipalities(value))
syncFilterValue(boroughs, value => activeView.value.setTransactionBoroughs(value))

watch(
	() => [activeView.value.view.value.scope, activeView.value.view.value.filter?.transaction] as const,
	([, filter]) => {
		isApplyingViewFilter = true
		assetTypes.value = filter?.assetTypes ?? []
		builtCategories.value = filter?.builtCategories ?? []
		builtSubcategories.value = filter?.builtSubcategories ?? []
		landCategories.value = filter?.landCategories ?? []
		landSubcategories.value = filter?.landSubcategories ?? []
		transactionStatuses.value = filter?.statuses ?? []
		transactionVerificationStatus.value = filter?.verificationStatus ?? null
		transactionBuyerStatus.value = filter?.buyerStatus ?? null
		transactionSource.value = filter?.source ?? null
		transactionComposition.value = filter?.composition ?? null
		superficyRange.value = rangeValue(filter?.landArea, [0, RANGE_SLIDER_MAX])
		constructibleRange.value = rangeValue(filter?.buildableLandArea, [0, RANGE_SLIDER_MAX])
		priceRange.value = rangeValue(filter?.price, [0, RANGE_SLIDER_MAX])
		residentialUnitsRange.value = rangeValue(filter?.residentialUnits, [
			residentialUnitsSliderMin,
			residentialUnitsSliderMax,
		])
		dateRange.value = {
			min: dateValue(filter?.date?.min),
			max: dateValue(filter?.date?.max),
		}
		regions.value = filter?.regions ?? []
		municipalities.value = filter?.municipalities ?? []
		boroughs.value = filter?.boroughs ?? []
		nextTick(() => {
			isApplyingViewFilter = false
		})
	},
	{ deep: true, immediate: true }
)

const resetFilters = () => {
	assetTypes.value = []
	builtCategories.value = []
	builtSubcategories.value = []
	landCategories.value = []
	landSubcategories.value = []
	transactionStatuses.value = []
	transactionVerificationStatus.value = null
	transactionBuyerStatus.value = null
	transactionSource.value = null
	transactionComposition.value = null
	superficyRange.value = [0, RANGE_SLIDER_MAX]
	constructibleRange.value = [0, RANGE_SLIDER_MAX]
	priceRange.value = [0, RANGE_SLIDER_MAX]
	residentialUnitsRange.value = [residentialUnitsSliderMin, residentialUnitsSliderMax]
	dateRange.value = { min: null, max: null }
	regions.value = []
	municipalities.value = []
	boroughs.value = []
	activeView.value.resetFilter()
}

defineExpose({ resetFilters })
</script>

<style scoped>
.transactions-map-filters {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		&:not(:last-child) {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			padding-bottom: var(--spacing-lg);
		}
	}

	h5 {
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}

	.range-filters {
		padding: 0;
		padding-bottom: var(--spacing-lg);

		.range {
			--input-width: var(--width-input-field);

			.values {
				margin-top: var(--spacing-sm);
			}
		}
	}

	.filters {
		min-height: var(--spacing-2xl);
		font-size: var(--font-size-small);
		width: 100%;
		position: relative;
	}
}
</style>
